<template>
  <div class="store-report poppins">
    <v-row no-gutters>
      <!-- ── Left Panel: Filters & History ────────────────────── -->
      <v-col cols="12" md="3">
        <div class="store-report__filter-card">
          <!-- Tabs Header -->
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            color="primary"
            grow
            hide-slider
            class="store-report__tabs"
            height="48"
          >
            <v-tab class="store-report__tab">
              <v-icon left size="18">mdi-filter-variant</v-icon>
              Parameters
            </v-tab>
            <v-tab class="store-report__tab">
              <v-icon left size="18">mdi-history</v-icon>
              History
              <v-chip
                v-if="reportStructure.history && reportStructure.history.length"
                x-small
                color="primary"
                text-color="white"
                class="ml-2"
              >
                {{ reportStructure.history.length }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items
            v-model="activeTab"
            style="background-color: transparent"
            class="mt-4"
          >
            <!-- ── Filters Tab ──────────────────────────────── -->
            <v-tab-item>
              <div
                v-for="filter in reportStructure.filters"
                :key="filter.name"
                class="store-report__field"
              >
                <label class="store-report__label">
                  <img
                    v-if="filter.icon"
                    :src="filter.icon"
                    :alt="filter.title"
                    class="store-report__label-icon"
                  />
                  {{ filter.title }}
                </label>

                <!-- select2Single -->
                <v-select
                  v-if="filter.type === 'select2Single'"
                  v-model="filterValues[filter.name]"
                  :items="filter.values"
                  item-text="text"
                  item-value="index"
                  dense
                  outlined
                  :placeholder="`Select ${filter.title}`"
                  hide-details
                  class="store-report__input"
                  :disabled="limit_finish"
                />
              </div>
              <!-- Dynamic Filters -->
              <!-- Actions -->
              <div :class="{ 'store-report__actions': !isClient }">
                <v-btn
                  color="primary"
                  depressed
                  block
                  class="store-report__analyze-btn"
                  @click="onAnalyze"
                  :disabled="limit_finish"
                >
                  <v-icon left size="18">mdi-chart-line</v-icon>
                  Recommend
                </v-btn>
              </div>

              <div class="store-report__summary mt-6">
                <div class="store-report__summary-header">
                  <v-icon size="18" color="#7e04b7" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  <span>Credits</span>
                </div>
                <p
                  class="store-report__summary-text"
                  v-if="limit && !limit_finish"
                >
                  You have {{ limit }} recommendations for this month.
                </p>
                <p v-else-if="limit_finish" class="store-report__summary-text">
                  You have reached your monthly report generation limit. You can
                  generate new reports next month.
                </p>
              </div>
            </v-tab-item>

            <!-- ── History Tab ──────────────────────────────── -->
            <v-tab-item>
              <div
                v-if="reportStructure.history && reportStructure.history.length"
              >
                <div class="store-report__history-list">
                  <div
                    v-for="entry in reportStructure.history"
                    :key="entry.id"
                    class="store-report__history-item"
                    :class="{
                      'store-report__history-item--active':
                        activeHistoryId === entry.id,
                    }"
                    @click="applyHistory(entry)"
                  >
                    <div class="store-report__history-chips">
                      <span
                        v-for="f in entry.filters"
                        :key="f.name"
                        class="store-report__history-chip"
                      >
                        {{ f.value_label }}
                      </span>
                    </div>
                    <span class="store-report__history-date">
                      {{ entry.created_at }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="py-10 text-center">
                <v-icon size="40" color="#e2e8f0" class="mb-3"
                  >mdi-history</v-icon
                >
                <div
                  class="store-report__empty-desc mx-auto"
                  style="font-size: 13px; color: #94a3b8"
                >
                  No history available yet.
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-col>

      <!-- ── Right Panel: Results ─────────────────────────────── -->
      <v-col cols="12" md="9">
        <div class="store-report__results">
          <!-- Empty State -->
          <div v-if="!outputData" class="store-report__empty">
            <v-icon size="64" color="#d1d5db">mdi-chart-box-outline</v-icon>
            <h3 class="store-report__empty-title">No results yet</h3>
            <p class="store-report__empty-desc">
              <span v-if="!isClient">Select your filters and click</span>
              <span v-else-if="isClient">Click&nbsp;</span>
              <strong>Analyze</strong> to generate the SKU forecast report.
            </p>
          </div>

          <!-- Results -->
          <div v-else class="store-report__list">
            <div class="store-report__list-header">
              <span class="store-report__list-title">
                <v-icon size="20" color="#7e04b7" class="mr-1"
                  >mdi-clipboard-text-outline</v-icon
                >
                Recommendations
              </span>

              <!-- Export -->
              <div
                v-if="reportStructure.export"
                class="store-report__summary-footer align-items-center"
              >
                <v-chip small color="primary" text-color="white" class="mx-2">
                  SKU: {{ outputData.sku }}
                </v-chip>

                <!--                <v-btn-->
                <!--                  small-->
                <!--                  depressed-->
                <!--                  rounded-->
                <!--                  outlined-->
                <!--                  color="primary"-->
                <!--                  class="store-report__download-btn mx-4"-->
                <!--                >-->
                <!--                  <v-icon left size="16">mdi-download</v-icon>-->
                <!--                  Export-->
                <!--                </v-btn>-->
              </div>
            </div>

            <!-- Summary Meta -->
            <div class="store-report__summary-inner">
              <!-- Summary -->
              <div v-if="outputData.summary" class="store-report__summary">
                <div class="store-report__summary-header">
                  <v-icon size="18" color="#7e04b7" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  <span>Summary</span>
                </div>
                <p class="store-report__summary-text">
                  {{ outputData.summary }}
                </p>
              </div>

              <!-- Export -->
              <!--              <div-->
              <!--                v-if="reportStructure.export"-->
              <!--                class="store-report__summary-footer"-->
              <!--              >-->
              <!--                <v-btn-->
              <!--                  small-->
              <!--                  depressed-->
              <!--                  color="primary"-->
              <!--                  class="store-report__download-btn"-->
              <!--                >-->
              <!--                  <v-icon left size="16">mdi-download</v-icon>-->
              <!--                  Export-->
              <!--                </v-btn>-->
              <!--              </div>-->
            </div>

            <!-- ── Dynamic Tables ──────────────────────────────── -->
            <div
              v-for="(table, tIdx) in outputData.tables"
              :key="'tbl-' + tIdx"
              class="sku-report__card sku-report__table-card"
            >
              <div class="sku-report__card-header">
                <span class="sku-report__card-title">
                  <v-icon size="22" color="#7e04b7" class="mr-1"
                    >mdi-table</v-icon
                  >
                  {{ table.title }}
                </span>
                <v-spacer />
                <v-text-field
                  v-model="tableSearches[tIdx]"
                  dense
                  outlined
                  :placeholder="$t('search')"
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  class="sku-report__table-search"
                />
              </div>
              <v-data-table
                :headers="getTableHeaders(table)"
                :items="getTableItems(table)"
                :search="tableSearches[tIdx]"
                :items-per-page="10"
                disable-sort
                mobile-breakpoint="40"
                class="sku-report__data-table"
                :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
              />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";

export default {
  name: "ReportProductRecommendation",

  data: () => ({
    // ── API-driven structure ────────────────────────────────
    reportStructure: {
      properties: { title: "", description: "" },
      filters: [],
      export: false,
      history: [],
    },

    // ── Filter values keyed by filter.name ──────────────────
    filterValues: {},
    entries: [],
    tableSearches: {},

    // ── Results state ───────────────────────────────────────
    outputData: null,
    activeHistoryId: null,

    limit: null,
    limit_finish: false,

    // ── Active Tab ──────────────────────────────────────────
    activeTab: 0,
  }),

  computed: {
    items: function () {
      return this.entries;
    },
    isClient: function () {
      // return true;
      return this.$store.getters.isClient;
    },
  },

  beforeMount() {
    this.fetchReportStructure();
  },
  methods: {
    /**
     * Fetch report configuration from API.
     * Populates filters, history, and auto-selects first dropdown values.
     */
    async fetchReportStructure(refresh = false) {
      this.$store.commit(SET_PAGE_LOADING, true);
      try {
        const { data } = await ApiService.post(
          "/reports/reportProductRecommendation"
        );
        if (data.success && data.data) {
          this.limit = data.data.limit;
          this.limit_finish = data.data.limit_finish;

          if (refresh) {
            this.reportStructure.history = data.data.history;
          } else {
            this.reportStructure = data.data;

            if (this.isClient) {
              this.reportStructure.filters = data.data.filters.filter(
                (item) => item.name !== "client"
              );
            }
            this.initFilterDefaults();
          }
        }
      } catch (e) {
        // console.error("Failed to fetch report structure:", e);
      } finally {
        this.$store.commit(SET_PAGE_LOADING, false);
      }
    },

    /**
     * Initialize filterValues with first option for selects, empty for inputs.
     */
    initFilterDefaults() {
      const defaults = {};
      this.reportStructure.filters.forEach((filter) => {
        if (
          filter.type === "select2Single" &&
          filter.values &&
          filter.values.length
        ) {
          defaults[filter.name] = filter.values[0].index;
        } else {
          defaults[filter.name] = filter.type === "inputText" ? "" : null;
        }
      });
      this.filterValues = defaults;
    },

    /**
     * Submit filters to API and display result.
     */
    async onAnalyze() {
      this.activeHistoryId = null;
      this.$store.commit(SET_PAGE_LOADING, true);

      const payload = {
        filters: Object.entries(this.filterValues)
          .filter(([, v]) => v !== null && v !== "")
          .map(([name, value]) => ({ name, value })),
      };

      try {
        const { data } = await ApiService.post(
          "/reports/reportProductRecommendation/show",
          payload
        );
        if (data && data.success && data.data) {
          this.outputData = data.data;
        }
      } catch (e) {
        // console.error("Failed to fetch forecast data:", e);
      } finally {
        // this.$store.commit(SET_PAGE_LOADING, false);
        await this.fetchReportStructure(true);
      }
    },

    /**
     * Apply a history entry: set filter values + output instantly.
     */
    applyHistory(entry) {
      if (this.activeHistoryId && this.activeHistoryId === entry.id) {
        this.activeHistoryId = null;
        this.outputData = null;
        this.initFilterDefaults();
        return;
      }
      this.activeHistoryId = entry.id;

      // Restore filter values
      const restored = {};
      this.reportStructure.filters.forEach((filter) => {
        const histFilter = entry.filters.find((f) => f.name === filter.name);
        restored[filter.name] = histFilter ? histFilter.value : null;
      });
      this.filterValues = restored;

      // Set output directly from history
      this.outputData = entry.output || null;
    },

    /**
     * Transform a table's headers array into v-data-table header objects.
     */
    getTableHeaders(table) {
      if (!table || !table.headers) return [];
      return table.headers.map((h) => ({
        text: h,
        value: h
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, ""),
        class: "text-muted fw-bolder font-size-sm text-uppercase poppins-ls",
        cellClass: "second-text",
      }));
    },

    /**
     * Transform a table's body 2D array into v-data-table row objects.
     */
    getTableItems(table) {
      if (!table || !table.body) return [];
      const headers = this.getTableHeaders(table);
      return table.body.map((row) => {
        const obj = {};
        row.forEach((cell, i) => {
          if (headers[i]) obj[headers[i].value] = cell;
        });
        return obj;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Variables ──────────────────────────────────────────────── */
$primary: #7e04b7;
$border: rgba(0, 0, 0, 0.06);
$radius: 14px;
$radius-sm: 10px;
$radius-xs: 8px;
$shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
$font: "Poppins", sans-serif;
$title-font-size: 17px;
$value-font-size: 15px;

/* ── Page wrapper ───────────────────────────────────────────── */
.store-report {
  padding: 0px 32px;

  @media (max-width: 960px) {
    padding: 0px 16px;
    overflow-x: hidden;
  }

  @media (max-height: 1000px) {
    padding: 0px 16px;
    overflow-x: hidden;
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
  }
}

/* ── Fix v-row horizontal overflow on mobile ────────────────── */
::v-deep .v-row {
  @media (max-width: 960px) {
    margin: 0 !important;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 100vh;
    -webkit-overflow-scrolling: touch;
  }
}

::v-deep .v-col {
  @media (max-width: 960px) {
    padding: 0 !important;
  }
}

/* ── Filter Card ────────────────────────────────────────────── */
.store-report__filter-card {
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  padding: 24px 20px;

  @media (max-width: 960px) {
    padding: 16px 12px;
    margin-bottom: 16px;
    position: static;
  }

  @media (max-height: 800px) {
    padding: 16px 12px;
    margin-bottom: 16px;
    position: static;
  }
}

.store-report__tabs {
  border-bottom: 1px solid $border;
  margin-bottom: 16px;

  ::v-deep .v-tabs-bar {
    height: 48px !important;
  }
}

.store-report__tab {
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
  color: #64748b !important;

  &.v-tab--active {
    color: $primary !important;
  }
}

.store-report__field {
  margin-bottom: 20px;
}

.store-report__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: $font;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.store-report__label-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.store-report__input ::v-deep .v-input__slot {
  border-radius: $radius-sm !important;
  min-height: 40px !important;
}

.store-report__actions {
  margin-top: 28px;
  //border-top: 1px solid $border;
}

.store-report__analyze-btn {
  font-family: $font;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  border-radius: $radius-sm !important;
  height: 42px !important;
  text-transform: none;
}

/* ── Generic Card ──────────────────────────────────────────── */
.sku-report__card {
  background: #ffffff;
  //border-radius: $radius;
  //box-shadow: $shadow;
  //border: 1px solid $border;
  overflow: hidden;
}

.sku-report__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  //border-bottom: 1px solid $border;
  flex-wrap: wrap;
  gap: 10px;
}

.sku-report__card-title {
  font-family: $font;
  font-size: $title-font-size;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

/* ── Table Section ──────────────────────────────────────────── */
.sku-report__table-card {
  margin-top: 20px;
  margin-bottom: 0;
}

.sku-report__table-search {
  max-width: 220px;

  ::v-deep .v-input__slot {
    border-radius: $radius-sm !important;
    min-height: 36px !important;
  }

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
  }
}

.sku-report__data-table {
  font-family: $font;

  ::v-deep {
    thead th {
      &:first-of-type {
        padding-inline-start: 20px !important;
      }
      //background: #f8fafc !important;
      border-bottom: 1px solid $border !important;
      white-space: nowrap;
    }

    tbody td {
      &:first-of-type {
        padding-inline-start: 20px !important;
      }

      font-family: $font !important;
      font-size: $value-font-size !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
      padding-top: 12px !important;
      padding-bottom: 12px !important;

      img.icon {
        height: 1.1rem;
        margin-inline-end: 3px;
        margin-bottom: 2px;
        vertical-align: middle;
      }
    }

    tbody tr:hover {
      background: rgba($primary, 0.02) !important;
    }

    .v-data-footer {
      font-family: $font;
      font-size: $value-font-size;
      color: #7e8299 !important;
      font-weight: 500 !important;
      border-top: 1px solid $border;
    }
  }
}

/* ── History Section ────────────────────────────────────────── */
.store-report__history-list {
  max-height: 60vh;
  overflow-y: auto;
  padding-inline-end: 4px;

  @media (max-width: 960px) {
    max-height: 300px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba($primary, 0.2);
    border-radius: 4px;
  }
}

.store-report__history-item {
  padding: 10px 12px;
  border-radius: $radius-sm;
  border: 1px solid $border;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: rgba($primary, 0.04);
    border-color: rgba($primary, 0.15);
  }

  &--active {
    background: rgba($primary, 0.06) !important;
    border-color: rgba($primary, 0.3) !important;
    box-shadow: 0 0 0 1px rgba($primary, 0.1);
  }
}

.store-report__history-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.store-report__history-chip {
  display: inline-block;
  font-family: $font;
  font-size: 11px;
  font-weight: 500;
  color: $primary;
  background: rgba($primary, 0.08);
  padding: 2px 8px;
  border-radius: 4px;
  line-height: 1.4;
}

.store-report__history-date {
  font-family: $font;
  font-size: 11px;
  color: #94a3b8;
}

/* ── Results Panel ──────────────────────────────────────────── */
.store-report__results {
  padding-inline-start: 24px;
  min-height: 400px;

  @media (max-height: 800px) {
    padding-inline-start: 12px;
  }

  @media (max-width: 960px) {
    padding-inline-start: 0;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: 600px) {
    padding-inline-start: 0;
    min-height: unset;
  }
}

/* Empty State */
.store-report__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  text-align: center;
  padding: 48px 24px;

  @media (max-width: 960px) {
    min-height: 300px;
    padding: 32px 16px;
  }

  @media (max-width: 560px) {
    min-height: 100px;
    padding: 24px 12px;
  }
}

.store-report__empty-title {
  font-family: $font;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 16px 0 6px;
}

.store-report__empty-desc {
  font-family: $font;
  font-size: 13px;
  color: #94a3b8;
  max-width: 300px;
  margin: 0;
  line-height: 1.5;
}

/* Results List */
.store-report__list {
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  //max-height: 85vh !important;
  margin-bottom: 30px;
  overflow-y: hidden;
}

.store-report__list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid $border;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 600px) {
    padding: 14px 14px;
  }
}

.store-report__list-title {
  font-family: $font;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.store-report__summary-inner {
  padding: 20px 20px 0px;

  @media (max-width: 600px) {
    padding: 16px 14px;
  }
}

/* Summary */
.store-report__summary {
  background: #f8fafc;
  border-radius: $radius-sm;
  border: 1px solid $border;
  padding: 16px;
  margin-bottom: 16px;
}

.store-report__summary-header {
  display: flex;
  align-items: center;
  font-family: $font;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.store-report__summary-text {
  font-family: $font;
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
}

/* Summary Footer */
.store-report__summary-footer {
  display: flex;
  justify-content: flex-end;
}

.store-report__download-btn {
  font-family: $font;
  font-size: 12px;
  font-weight: 600;
  //border-radius: 8px !important;
  text-transform: none;
  letter-spacing: 0;
}
</style>
